import React, { useEffect, useState } from 'react'
import { t } from 'i18next'
import { ShopCheckoutSideBarResponse, useShopCart } from 'src/Libs/shop'
import { useTypedSelector } from 'src/State/RootReducer'
import useModals from 'src/Hooks/useModals'
import { ProductGeneralType, ProductType, ProgressBarSteps } from 'src/Libs/shop/types'
import { useEffectShopAccess } from 'src/Libs/shop/hooks'
import { getBasketQntmConfigurationIds } from 'src/Libs/shop/utils'
import SystemConfiguration from '../SystemConfiguration'
import { useQntmSystemConfigurationContext } from 'src/Libs/shop/context/use-shop-qntm-system-configuration-context'
import QntmBasicSystemWrapper from './BasicSystem/QntmBasicSystemWrapper'
import QntmMainComponent from './MainComponent/QntmMainComponent'
import QntmAdditionalComponent from './AdditionalComponent/QntmAdditionalComponent'
import QntmSystemConfigurationCard from './SystemConfigurationCard/QntmSystemConfigurationCard'
import QntmBottomProgressBar from './BottomProgressBar/QntmBottomProgressBar'
import QntmReminder from './BasicSystem/QntmReminder'

const SHOP_LOCAL_STORAGE_KEY = 'persist:shop'

const QntmSystemConfigurationWrapper: React.FC = () => {
  const { openShopHintModal } = useModals()
  const { getCheckoutProducts } = useShopCart()
  const basketQuantity = useTypedSelector(state => state.shop.basketQuantity)
  const qntmReminder = useTypedSelector(state => state.shop.qntmReminder)
  const { activePage, handleProgress, allowContinueMain } = useQntmSystemConfigurationContext()
  const [cartData, setCartData] = useState<ShopCheckoutSideBarResponse | null>(null)

  const hasBasketQntmConfigurator = cartData?.lineItems.find(product => product.type === ProductGeneralType.QNTM_SYSTEM_CONFIGURATOR)
  const cartDesiredShippingDate = cartData?.extensions?.e3dcDesiredShippingDate ?? null
  const existingSystemConfigurationDate = hasBasketQntmConfigurator ? cartDesiredShippingDate : null
  const existingSystemConfigurationIds = getBasketQntmConfigurationIds(cartData)
  const existingSystemConfigurationType = hasBasketQntmConfigurator?.payload.quantityType

  const hasBasketOtherProductType = cartData?.lineItems?.some(product => {
    const isQntmConfigurator = product.type === ProductGeneralType.QNTM_SYSTEM_CONFIGURATOR
    const isQntmProduct = product.type === ProductGeneralType.PRODUCT && product.payload?.e3dc?.productType === ProductType.QntmProduct

    return !(isQntmConfigurator || isQntmProduct)
  })

  useEffect(() => {
    if (activePage === ProgressBarSteps.BasicSystem && hasBasketOtherProductType) {
      openShopHintModal({
        text: t('shop.note.basket-has-other-product-type'),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, cartData])

  useEffectShopAccess(() => {
    const canStoreLocaly = localStorage.getItem(SHOP_LOCAL_STORAGE_KEY)

    if (activePage === ProgressBarSteps.BasicSystem && qntmReminder) {
      openShopHintModal({
        text: t('shop.note.qntm-reminder-note'),
        additionalActionsLeft: canStoreLocaly ? <QntmReminder /> : null,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffectShopAccess(() => {
    if (activePage === ProgressBarSteps.BasicSystem) {
      getCheckoutProducts()
        .then(result => {
          if (result) {
            setCartData(result)
          }
        })
        .catch(error => console.error(error))
    }
  }, [basketQuantity, activePage])

  const steps = (): React.ReactNode => {
    switch (activePage) {
      case ProgressBarSteps.BasicSystem:
        return (
          <QntmBasicSystemWrapper
            existingSystemConfigurationType={existingSystemConfigurationType}
            desiredShippingDate={existingSystemConfigurationDate}
            cartSystemConfigurationIds={existingSystemConfigurationIds}
          />
        )

      case ProgressBarSteps.MainComponent:
        return <QntmMainComponent />

      case ProgressBarSteps.AdditionalComponent:
        return <QntmAdditionalComponent />

      default:
        return null
    }
  }

  const renderSidebarCard = (onClose: () => void): React.ReactNode => (
    <QntmSystemConfigurationCard showMobile={false} onClose={onClose} navigateTo={handleProgress} progress={activePage} />
  )

  const renderMobileSidebarCard = (onClose: () => void): React.ReactNode => (
    <QntmSystemConfigurationCard showMobile onClose={onClose} navigateTo={handleProgress} progress={activePage} />
  )

  return (
    <SystemConfiguration
      content={steps()}
      bottomBar={<QntmBottomProgressBar progress={activePage} handleProgress={handleProgress} allProductsSelected={allowContinueMain} handleBack />}
      sidebarCard={renderSidebarCard}
      mobileSidebarCard={renderMobileSidebarCard}
    />
  )
}

export default QntmSystemConfigurationWrapper
